import type { InfiniteQueryKeyData, InfiniteQueryParams } from '@pba/react-query';
import type { ChatroomDataObject, ChatroomTag } from '#models/services';
import { useInfiniteQuery } from '#react-query';
import { getChatrooms } from '../../services';
import { getChatroomsInfiniteQueryKey } from './keys';

export type ChatroomsInfiniteQueryData = InfiniteQueryKeyData<
  Array<ChatroomDataObject | null>,
  typeof getChatroomsInfiniteQueryKey
>;

export type GetChatroomsQueryFilter = {
  search?: string;
  tags?: ChatroomTag;
  unread?: boolean;
};

export type GetChatroomsQueryParams = InfiniteQueryParams<ChatroomsInfiniteQueryData, GetChatroomsQueryFilter>;

export const useGetChatrooms = ({ filter, options, pagination }: GetChatroomsQueryParams) => {
  const pageSize = pagination?.pageSize ?? 15;

  return useInfiniteQuery({
    async queryFn({ pageParam }) {
      const response = await getChatrooms({
        config: {
          params: {
            $limit: pageSize,
            $search: filter.search,
            $skip: pageParam ?? 0,
            $sort: {
              updatedAt: '-1',
            },
            tags: filter.tags,
            unread: filter.unread,
          },
        },
      });

      return response.data.data;
    },
    initialPageParam: 0,
    getNextPageParam(lastPage, allPages) {
      if (lastPage.length < pageSize) {
        return undefined;
      }

      return allPages.flat().filter((chatroom) => chatroom !== null).length;
    },
    queryKey: getChatroomsInfiniteQueryKey({ filter, pagination }),
    ...options,
  });
};
